import { useContext, useEffect } from "react";
import {
  Wallet, ArrowDownCircle, ArrowUpCircle, CreditCard, Send,
  TrendingUp, Clock, Loader2,
} from "lucide-react";
import { Link } from "react-router-dom";
import { UserContext } from "../context/UserContext";
import getInitials from "../utils/getInitials";

const KYC_STYLES = {
  Approved: "bg-emerald-500/15 border border-emerald-500/30 text-emerald-300",
  Pending: "bg-amber-500/15 border border-amber-500/30 text-amber-300",
  Rejected: "bg-red-500/15 border border-red-500/30 text-red-300",
};
const KYC_LABELS = {
  Approved: "✓ KYC Verified",
  Pending: "⏳ KYC Pending",
  Rejected: "✗ KYC Rejected",
};

const TX_TYPE_LABEL = {
  deposit: "Deposit",
  withdrawal: "Withdrawal",
  transfer: "Transfer",
  bill: "Bill Payment",
  // crypto_deposit: "Crypto Deposit",
  // crypto_withdrawal: "Crypto Withdrawal",
};

const MAX_RECENT_ITEMS = 5;

const isInflow = (tx) => {
  if (tx.type === "deposit") return true;
  // if (["deposit", "crypto_deposit"].includes(tx.type)) return true;
  if (tx.type === "transfer" && tx.metadata?.direction === "in") return true;
  return false;
};

const getAmount = (tx) => {
  const amount = Number(tx?.amount);
  return Number.isFinite(amount) ? amount : 0;
};

export default function UserDashboard() {
  const { user, wallet, transactions, loading, refreshUserData } = useContext(UserContext);

  useEffect(() => {
    refreshUserData();
  }, [refreshUserData]);

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="animate-spin text-cyan-300" size={36} />
      </div>
    );
  }

  const inflowTransactions = transactions
    .filter((tx) => tx?.status !== "failed" && getAmount(tx) > 0 && (
      tx?.type === "deposit" || (tx?.type === "transfer" && tx?.metadata?.direction === "in")
    ))
    .slice(0, MAX_RECENT_ITEMS);

  const outflowTransactions = transactions
    .filter((tx) => tx?.status !== "failed" && getAmount(tx) > 0 && (
      tx?.type === "withdrawal" || (tx?.type === "transfer" && tx?.metadata?.direction === "out")
    ))
    .slice(0, MAX_RECENT_ITEMS);

  const totalInflow = inflowTransactions.reduce((sum, tx) => sum + getAmount(tx), 0);
  const totalOutflow = outflowTransactions.reduce((sum, tx) => sum + getAmount(tx), 0);
  const peakFlowAmount = Math.max(
    ...inflowTransactions.map(getAmount),
    ...outflowTransactions.map(getAmount),
    1
  );

  const recentTx = transactions.slice(0, MAX_RECENT_ITEMS);

  const stats = [
    {
      label: "Wallet Balance",
      value: wallet.balance,
      icon: Wallet,
      iconColor: "text-cyan-300",
      bg: "bg-cyan-300/10",
      sub: "Available balance",
    },
    {
      label: "Total Deposits",
      value: wallet.deposits,
      icon: ArrowDownCircle,
      iconColor: "text-emerald-400",
      bg: "bg-emerald-400/10",
      sub: "All time",
    },
    {
      label: "Total Withdrawals",
      value: wallet.withdrawals,
      icon: ArrowUpCircle,
      iconColor: "text-red-400",
      bg: "bg-red-400/10",
      sub: "All time",
    },
  ];

  const quickActions = [
    {
      label: "Fund Wallet",
      to: "/fund",
      icon: ArrowDownCircle,
      color: "bg-cyan-300/10 border-cyan-300/20 text-cyan-300 hover:bg-cyan-300/20",
    },
    {
      label: "Withdraw",
      to: "/send-money",
      icon: ArrowUpCircle,
      color: "bg-red-400/10 border-red-400/20 text-red-400 hover:bg-red-400/20",
    },
    {
      label: "Pay Bills",
      to: "/pay-bills",
      icon: CreditCard,
      color: "bg-amber-400/10 border-amber-400/20 text-amber-400 hover:bg-amber-400/20",
    },
    {
      label: "Send Money",
      to: "/send-money",
      icon: Send,
      color: "bg-violet-400/10 border-violet-400/20 text-violet-400 hover:bg-violet-400/20",
    },
  ];

  return (
    <div className="space-y-6 text-white">
      {/* Greeting */}
      <div className="relative overflow-hidden rounded-[2rem] border border-white/10 bg-slate-900/75 p-6 backdrop-blur-xl">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(37,99,235,0.25),_transparent_55%)]" />
        <div className="relative flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div
              aria-label={`${user.name || "User"} initials`}
              className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-full border-2 border-cyan-300/25 bg-cyan-300/10 text-lg font-semibold text-cyan-200"
            >
              {getInitials(user.name)}
            </div>
            <div>
              <p className="text-sm text-slate-400">Welcome back</p>
              <h1 className="text-2xl font-bold text-white">
                Hello, {user.name || "there"} 👋
              </h1>
            </div>
          </div>
          {user.kycStatus && (
            <span
              className={`rounded-full px-3 py-1 text-xs font-semibold ${
                KYC_STYLES[user.kycStatus] || "bg-slate-700 text-slate-300"
              }`}
            >
              {KYC_LABELS[user.kycStatus] || user.kycStatus}
            </span>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {stats.map(({ label, value, icon: Icon, iconColor, bg, sub }) => (
          <div
            key={label}
            className="rounded-[2rem] border border-white/10 bg-slate-900/75 p-6 backdrop-blur-xl"
          >
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-slate-300">{label}</p>
              <span className={`flex h-10 w-10 items-center justify-center rounded-xl ${bg}`}>
                <Icon className={iconColor} size={20} />
              </span>
            </div>
            <p className="mt-4 text-3xl font-bold text-white">
              ₦{Number(value).toLocaleString()}
            </p>
            <p className="mt-1 text-xs text-slate-400">{sub}</p>
          </div>
        ))}
      </div>

      {/* Inflow / Outflow */}
      <div className="rounded-[2rem] border border-white/10 bg-slate-900/75 p-6 backdrop-blur-xl">
        <div className="mb-6 flex items-center gap-2">
          <TrendingUp className="text-cyan-300" size={20} />
          <h2 className="text-base font-semibold text-white">Inflow & Outflow</h2>
          <div className="ml-auto flex items-center gap-4 text-xs text-slate-400">
            <span className="flex items-center gap-1"><span className="inline-block h-2 w-4 rounded bg-emerald-400" /> Inflow</span>
            <span className="flex items-center gap-1"><span className="inline-block h-2 w-4 rounded bg-rose-400" /> Outflow</span>
          </div>
        </div>
        <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="relative overflow-hidden rounded-[1.5rem] border border-emerald-400/20 bg-emerald-400/10 p-5">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(16,185,129,0.18),_transparent_55%)]" />
            <div className="relative flex items-start justify-between gap-4">
              <div>
                <p className="text-xs uppercase tracking-[0.24em] text-emerald-200/80">Inflow</p>
                <p className="mt-3 text-3xl font-bold text-white">₦{totalInflow.toLocaleString()}</p>
                <p className="mt-2 text-sm text-emerald-100/80">Deposits and money received</p>
              </div>
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-400/15 text-emerald-300">
                <ArrowDownCircle size={24} />
              </span>
            </div>
          </div>

          <div className="relative overflow-hidden rounded-[1.5rem] border border-rose-400/20 bg-rose-400/10 p-5">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(244,63,94,0.18),_transparent_55%)]" />
            <div className="relative flex items-start justify-between gap-4">
              <div>
                <p className="text-xs uppercase tracking-[0.24em] text-rose-200/80">Outflow</p>
                <p className="mt-3 text-3xl font-bold text-white">₦{totalOutflow.toLocaleString()}</p>
                <p className="mt-2 text-sm text-rose-100/80">Withdrawals and money sent</p>
              </div>
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-rose-400/15 text-rose-300">
                <ArrowUpCircle size={24} />
              </span>
            </div>
          </div>
        </div>
        {inflowTransactions.length === 0 && outflowTransactions.length === 0 ? (
          <p className="py-10 text-center text-sm text-slate-500">No deposit or withdrawal data yet.</p>
        ) : (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="rounded-[1.5rem] border border-white/10 bg-white/5 p-4">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-white">Recent Inflow</h3>
                  <p className="text-xs text-slate-400">Deposits and received transfers</p>
                </div>
                <span className="rounded-full bg-emerald-400/10 px-3 py-1 text-xs font-semibold text-emerald-300">
                  {inflowTransactions.length}
                </span>
              </div>
              <div className="space-y-3">
                {inflowTransactions.length === 0 ? (
                  <p className="py-6 text-center text-sm text-slate-500">No inflow yet.</p>
                ) : inflowTransactions.map((tx) => {
                  const amount = getAmount(tx);
                  const width = `${Math.max((amount / peakFlowAmount) * 100, 12)}%`;
                  return (
                    <div key={tx._id} className="rounded-xl border border-white/10 bg-slate-950/50 p-3">
                      <div className="mb-2 flex items-center justify-between gap-3">
                        <div>
                          <p className="text-sm font-semibold text-white">₦{amount.toLocaleString()}</p>
                          <p className="text-xs text-slate-400">
                            {new Date(tx.createdAt).toLocaleString("en-NG", {
                              day: "numeric",
                              month: "short",
                              hour: "numeric",
                              minute: "2-digit",
                            })}
                          </p>
                        </div>
                        <span className="rounded-full bg-emerald-400/10 px-2.5 py-1 text-[11px] font-semibold text-emerald-300">
                          {tx.type === "transfer" ? "Transfer In" : "Deposit"}
                        </span>
                      </div>
                      <div className="h-2 overflow-hidden rounded-full bg-slate-800">
                        <div className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-cyan-400" style={{ width }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="rounded-[1.5rem] border border-white/10 bg-white/5 p-4">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-white">Recent Outflow</h3>
                  <p className="text-xs text-slate-400">Withdrawals and sent transfers</p>
                </div>
                <span className="rounded-full bg-rose-400/10 px-3 py-1 text-xs font-semibold text-rose-300">
                  {outflowTransactions.length}
                </span>
              </div>
              <div className="space-y-3">
                {outflowTransactions.length === 0 ? (
                  <p className="py-6 text-center text-sm text-slate-500">No outflow yet.</p>
                ) : outflowTransactions.map((tx) => {
                  const amount = getAmount(tx);
                  const width = `${Math.max((amount / peakFlowAmount) * 100, 12)}%`;
                  return (
                    <div key={tx._id} className="rounded-xl border border-white/10 bg-slate-950/50 p-3">
                      <div className="mb-2 flex items-center justify-between gap-3">
                        <div>
                          <p className="text-sm font-semibold text-white">₦{amount.toLocaleString()}</p>
                          <p className="text-xs text-slate-400">
                            {new Date(tx.createdAt).toLocaleString("en-NG", {
                              day: "numeric",
                              month: "short",
                              hour: "numeric",
                              minute: "2-digit",
                            })}
                          </p>
                        </div>
                        <span className="rounded-full bg-rose-400/10 px-2.5 py-1 text-[11px] font-semibold text-rose-300">
                          {tx.type === "transfer" ? "Transfer Out" : "Withdrawal"}
                        </span>
                      </div>
                      <div className="h-2 overflow-hidden rounded-full bg-slate-800">
                        <div className="h-full rounded-full bg-gradient-to-r from-rose-500 to-orange-400" style={{ width }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="rounded-[2rem] border border-white/10 bg-slate-900/75 p-6 backdrop-blur-xl">
        <h2 className="mb-4 text-base font-semibold text-white">Quick Actions</h2>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          {quickActions.map(({ label, to, icon: Icon, color }) => (
            <Link
              key={to}
              to={to}
              className={`flex flex-col items-center justify-center gap-2 rounded-2xl border py-5 text-sm font-semibold transition ${color}`}
            >
              <Icon size={22} />
              {label}
            </Link>
          ))}
        </div>
      </div>

      {/* Recent Transactions */}
      <div className="rounded-[2rem] border border-white/10 bg-slate-900/75 p-6 backdrop-blur-xl">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Clock className="text-cyan-300" size={20} />
            <h2 className="text-base font-semibold text-white">Recent Transactions</h2>
          </div>
          <Link to="/transactions" className="text-xs text-cyan-300 hover:underline">
            View all →
          </Link>
        </div>
        {recentTx.length === 0 ? (
          <p className="py-8 text-center text-sm text-slate-500">No transactions yet.</p>
        ) : (
          <div className="space-y-3">
            {recentTx.map((tx) => (
              <div
                key={tx._id}
                className="flex items-center justify-between rounded-xl border border-white/10 bg-white/5 px-4 py-3"
              >
                <div className="flex items-center gap-3">
                  <span
                    className={`flex h-9 w-9 items-center justify-center rounded-full ${
                      isInflow(tx)
                        ? "bg-emerald-400/15 text-emerald-400"
                        : "bg-red-400/15 text-red-400"
                    }`}
                  >
                    {isInflow(tx) ? (
                      <ArrowDownCircle size={18} />
                    ) : (
                      <ArrowUpCircle size={18} />
                    )}
                  </span>
                  <div>
                    <p className="text-sm font-medium text-white">
                      {TX_TYPE_LABEL[tx.type] || tx.type}
                    </p>
                    <p className="text-xs text-slate-400">
                      {new Date(tx.createdAt).toLocaleDateString("en-NG", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      })}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p
                    className={`font-semibold ${
                      isInflow(tx) ? "text-emerald-400" : "text-red-400"
                    }`}
                  >
                    {isInflow(tx) ? "+" : "-"}₦{Number(tx.amount).toLocaleString()}
                  </p>
                  <span
                    className={`text-xs ${
                      tx.status === "success"
                        ? "text-emerald-400"
                        : tx.status === "pending"
                        ? "text-amber-400"
                        : "text-red-400"
                    }`}
                  >
                    {tx.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
